import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TypedMemo } from '../../../common/types';
import { SMLLink } from '../SMLLink/SMLLink';
import styles from './SMLResultsCard.module.scss';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { EmptyResults } from '../EmptyResults/EmptyResults';
import { SMLPagination } from '../SMLPagination/SMLPagination';
import { SMLResultsCardProps } from './props';
import { SMLImage } from '../SMLImage';
import { PlaylistImageMosaic } from '../PlaylistImageMosaic/PlaylistImageMosaic';
import { MiniScoreChart } from '../MiniScoreChart/MiniScoreChart';
import { SMLCountrySelector } from '../SMLCountrySelector/SMLCountrySelector';
import { SMLGenreSelector } from '../SMLGenreSelector/SMLGenreSelector';
import { Panel, PanelBody, PanelHead } from '../primitives/Panel';

const SMLResultsCardComponent: React.FC<SMLResultsCardProps> = ({
  title,
  subtitle,
  icon,
  iconColor,
  items,
  isLoading,
  error,
  onRetry,
  imageShape = 'circle',
  showCountryFilter = false,
  showGenreFilter = false,
  paginationParamName,
  hasMore,
}) => {
  return (
    <Panel className={styles.resultsCard}>
      <PanelHead
        titleAs="h2"
        icon={
          icon !== undefined ? (
            <FontAwesomeIcon
              icon={icon}
              className={styles.cardIcon}
              style={iconColor !== undefined ? { color: iconColor } : undefined}
            />
          ) : undefined
        }
        title={title}
        right={
          showGenreFilter || showCountryFilter || Boolean(subtitle) ? (
            <>
              {showGenreFilter && <SMLGenreSelector />}
              {showCountryFilter && <SMLCountrySelector />}
              {subtitle && <span className={styles.cardMeta}>{subtitle}</span>}
            </>
          ) : null
        }
      />
      <PanelBody>
        <div className={`d-flex flex-column ${styles.cardContent}`}>
          {error && (
            <ErrorView
              error={error}
              retry={() => {
                if (onRetry) {
                  onRetry();
                }
              }}
            />
          )}
          {items.length === 0 && isLoading && <LoadingView />}
          {!error && !isLoading && items.length === 0 && <EmptyResults />}
          {items.length > 0 &&
            items.map((item) => (
              <SMLLink
                key={item.id}
                to={item.route}
                params={item.params}
                className={`${styles.artistRow} d-flex justify-content-between align-items-center border-bottom text-decoration-none`}
                style={{ color: 'inherit' }}
              >
                <div className="d-flex align-items-center gap-1" style={{ minWidth: 0, flex: 1 }}>
                  <div
                    className={`${styles.artistRank} d-flex align-items-center justify-content-center`}
                    style={{ flexShrink: 0 }}
                  >
                    {item.position}
                  </div>
                  <div className="d-flex align-items-center gap-2" style={{ minWidth: 0, flex: 1 }}>
                    {item.trackImageUrls !== undefined && item.trackImageUrls.length > 0 ? (
                      <PlaylistImageMosaic
                        trackImageUrls={item.trackImageUrls}
                        fallbackImageUrl={item.imageUrl}
                        name={item.name}
                        size={32}
                      />
                    ) : (
                      <SMLImage
                        src={item.imageUrl}
                        alt={item.name}
                        width={32}
                        makeSquareFit
                        className={imageShape === 'circle' ? 'rounded-circle' : 'rounded-2'}
                        style={{
                          width: '32px',
                          height: '32px',
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <span className={styles.artistName}>{item.name}</span>
                  </div>
                </div>
                <div className={`${styles.rowRight} d-flex align-items-center`} style={{ flexShrink: 0 }}>
                  {item.chart !== undefined && (
                    <div className={styles.rowChart}>
                      <MiniScoreChart entityType={item.chart.entityType} entityId={item.id} date={item.chart.date} />
                    </div>
                  )}
                  {item.rightElement}
                </div>
              </SMLLink>
            ))}
        </div>

        <div className="border-top pt-2 mt-2">
          <SMLPagination paramName={paginationParamName} hasMore={hasMore} />
        </div>
      </PanelBody>
    </Panel>
  );
};

export const SMLResultsCard = TypedMemo(SMLResultsCardComponent);
